import type { Avatar, Icon } from "@dust-tt/sparkle";
import {
  ActionFrame,
  ActionIcons,
  AdomikLogo,
  AhrefsLogo,
  AirbyteLogo,
  AirtableLogo,
  AmplitudeLogo,
  Announcement01,
  ApifyLogo,
  AsanaLogo,
  AshbyLogo,
  Atom01,
  AttioLogo,
  BigQueryLogo,
  Brain,
  BrightDataLogo,
  Building02,
  CanvaLogo,
  CheckCircle,
  ClariLogo,
  ClayLogo,
  ClickUpLogo,
  Clock,
  CloudArrowLeftRight,
  ConfluenceLogo,
  ContentfulLogo,
  ContentsquareLogo,
  CostoryLogo,
  CursorLogo,
  cn,
  DatabricksLogo,
  DriveLogo,
  DropboxLogo,
  FaceSmile,
  FathomLogo,
  FigmaLogo,
  File06,
  FirefliesLogo,
  FreshserviceLogo,
  FrontLogo,
  FullEnrichLogo,
  GammaLogo,
  GcalLogo,
  GitBranch01,
  GithubLogo,
  GitlabLogo,
  Globe01,
  GmailLogo,
  GongLogo,
  GoogleSpreadsheetLogo,
  GrafanaLogo,
  GranolaLogo,
  GuruLogo,
  HexLogo,
  HubspotLogo,
  Image01,
  IntercomLogo,
  JiraLogo,
  LemlistLogo,
  Lightbulb04,
  LinearLogo,
  ListSelect,
  Lock01,
  LokaliseLogo,
  LumaLogo,
  MessageCircle01,
  MessageDotsCircle,
  MessageSmileCircle,
  MicrosoftExcelLogo,
  MicrosoftLogo,
  MicrosoftOutlookLogo,
  MicrosoftTeamsLogo,
  MiroLogo,
  MobbinLogo,
  ModjoLogo,
  MondayLogo,
  N8NLogo,
  NaptaLogo,
  NetSuiteLogo,
  NotionLogo,
  OpenaiLogo,
  PaddleLogo,
  PandaDocLogo,
  PieChart01,
  PowerBiLogo,
  PraizLogo,
  PresentationChart01,
  ProductboardLogo,
  PuzzlePiece01,
  QobraLogo,
  QontoLogo,
  Robot,
  SalesforceLogo,
  SalesloftLogo,
  Scan,
  SearchMd,
  SemrushLogo,
  ShapesPlus,
  ShopifyLogo,
  SiitLogo,
  SlabLogo,
  SlackLogo,
  SnowflakeLogo,
  Avatar as SparkleAvatar,
  SpendeskLogo,
  StackOneLogo,
  StatuspageLogo,
  StripeLogo,
  SupabaseLogo,
  SuperhumanLogo,
  SwarmiaLogo,
  Table,
  Terminal,
  TerminalSquare,
  UkgLogo,
  ValTownLogo,
  VantaLogo,
  VolumeMax,
  WebflowLogo,
  YoutrustLogo,
  ZendeskLogo,
} from "@dust-tt/sparkle";
import type { ComponentProps, ComponentType } from "react";

// Re-export icon names, types, and type guards from the sparkle-free module so
// that existing imports from this file continue to work.
export type {
  CustomResourceIconType,
  InternalAllowedIconType,
} from "@app/types/resources_icon_names";
export {
  isCustomResourceIconType,
  isInternalAllowedIcon,
} from "@app/types/resources_icon_names";

import type {
  CustomResourceIconType,
  InternalAllowedIconType,
} from "@app/types/resources_icon_names";
import { isCustomResourceIconType } from "@app/types/resources_icon_names";

interface ResourceAvatarProps extends ComponentProps<typeof Avatar> {}

type ResourceAvatarSize = NonNullable<ComponentProps<typeof Avatar>["size"]>;

const AVATAR_BADGE_CLASSES: Record<
  ResourceAvatarSize,
  { badge: string; icon: string }
> = {
  "3xs": { badge: "h-2.5 w-2.5 rounded-xs", icon: "h-1.5 w-1.5" },
  xxs: { badge: "h-3 w-3 rounded-xs", icon: "h-2 w-2" },
  xs: { badge: "h-3.5 w-3.5 rounded-sm", icon: "h-2.5 w-2.5" },
  sm: { badge: "h-4 w-4 rounded-sm", icon: "h-3 w-3" },
  md: { badge: "h-5 w-5 rounded-sm", icon: "h-3.5 w-3.5" },
  lg: { badge: "h-6 w-6 rounded-md", icon: "h-4 w-4" },
  xl: { badge: "h-7 w-7 rounded-md", icon: "h-5 w-5" },
  "2xl": { badge: "h-9 w-9 rounded-lg", icon: "h-7 w-7" },
  auto: { badge: "h-7 w-7 rounded-md", icon: "h-5 w-5" },
};

interface ResourceAvatarWithBadgeProps extends ResourceAvatarProps {
  badgeIcon: ComponentType<{ className?: string }>;
  badgeSize: ResourceAvatarSize;
}

/**
 * As Avatar are not made to support dark/light mode switch, this renders a `Avatar` component for resources icons with support for dark mode.
 * If `iconColor` or `backgroundColor` are not provided, sensible defaults are applied for both light and dark themes.
 */
export function ResourceAvatar({
  iconColor,
  backgroundColor,
  ...props
}: ResourceAvatarProps) {
  return (
    <SparkleAvatar
      iconColor={iconColor ?? "text-foreground"}
      backgroundColor={backgroundColor ?? "bg-muted-background"}
      {...props}
    />
  );
}

export function ResourceAvatarWithBadge({
  badgeIcon: BadgeIcon,
  badgeSize,
  className,
  ...props
}: ResourceAvatarWithBadgeProps) {
  const badgeClasses = AVATAR_BADGE_CLASSES[badgeSize];

  return (
    <div className={cn("relative inline-flex overflow-visible", className)}>
      <ResourceAvatar className={className} {...props} />
      <span
        className={cn(
          "pointer-events-none absolute bottom-0 right-0",
          "flex items-center justify-center bg-background shadow-sm ring-1 ring-border",
          "",
          badgeClasses.badge
        )}
      >
        <BadgeIcon className={badgeClasses.icon} />
      </span>
    </div>
  );
}

// Compile-time check: ensure CUSTOM_RESOURCE_ALLOWED matches ActionIcons keys.
// If ActionIcons gains or loses a key, this assignment will fail to compile.
const _customCheck: Record<CustomResourceIconType, unknown> = ActionIcons;
void _customCheck;

export const InternalActionIcons = {
  ActionAtomIcon: Atom01,
  ActionBrainIcon: Brain,
  ActionChatBubbleBottomCenterTextIcon: MessageCircle01,
  ActionChatBubbleThoughtIcon: MessageDotsCircle,
  ActionCheckCircleIcon: CheckCircle,
  ActionCloudArrowLeftRightIcon: CloudArrowLeftRight,
  ActionDocumentTextIcon: File06,
  ActionEmotionLaughIcon: FaceSmile,
  ActionFrameIcon: ActionFrame,
  ActionGitBranchIcon: GitBranch01,
  ActionGlobeAltIcon: Globe01,
  ActionImageIcon: Image01,
  ActionLightbulbIcon: Lightbulb04,
  ActionListCheckIcon: ListSelect,
  ActionLockIcon: Lock01,
  ActionMagnifyingGlassIcon: SearchMd,
  ActionMegaphoneIcon: Announcement01,
  ActionNoiseIcon: VolumeMax,
  ActionPieChartIcon: PieChart01,
  ActionRobotIcon: Robot,
  ActionScanIcon: Scan,
  ActionSlideshowIcon: PresentationChart01,
  ActionSpeakIcon: MessageSmileCircle,
  ActionStoreIcon: Building02,
  ActionTableIcon: Table,
  ActionTimeIcon: Clock,
  AdomikLogo,
  AhrefsLogo,
  AirbyteLogo,
  AirtableLogo,
  AmplitudeLogo,
  ApifyLogo,
  AsanaLogo,
  AttioLogo,
  AshbyLogo,
  BigQueryLogo,
  BrightDataLogo,
  ToolsIcon: ShapesPlus,
  CanvaLogo,
  ClariLogo,
  ClayLogo,
  ClickUpLogo,
  CommandLineIcon: Terminal,
  ConfluenceLogo,
  ContentfulLogo,
  ContentsquareLogo,
  CostoryLogo,
  CursorLogo,
  DatabricksLogo,
  DriveLogo,
  DropboxLogo,
  FathomLogo,
  FigmaLogo,
  FirefliesLogo,
  FreshserviceLogo,
  FrontLogo,
  FullEnrichLogo,
  GammaLogo,
  GcalLogo,
  GithubLogo,
  GitlabLogo,
  GmailLogo,
  GongLogo,
  GoogleSpreadsheetLogo,
  GrafanaLogo,
  GranolaLogo,
  GuruLogo,
  HexLogo,
  HubspotLogo,
  IntercomLogo,
  JiraLogo,
  LemlistLogo,
  LinearLogo,
  LokaliseLogo,
  LumaLogo,
  MicrosoftExcelLogo,
  MicrosoftLogo,
  MicrosoftOutlookLogo,
  MicrosoftTeamsLogo,
  MiroLogo,
  MobbinLogo,
  ModjoLogo,
  MondayLogo,
  N8NLogo,
  NaptaLogo,
  NetSuiteLogo,
  NotionLogo,
  OpenaiLogo,
  PaddleLogo,
  PandaDocLogo,
  PowerBiLogo,
  PraizLogo,
  ProductboardLogo,
  PuzzleIcon: PuzzlePiece01,
  QobraLogo,
  QontoLogo,
  SalesforceLogo,
  SemrushLogo,
  SiitLogo,
  SalesloftLogo,
  ShopifyLogo,
  SlabLogo,
  SlackLogo,
  SnowflakeLogo,
  SpendeskLogo,
  StackOneLogo,
  StatuspageLogo,
  StripeLogo,
  SupabaseLogo,
  SuperhumanLogo,
  SwarmiaLogo,
  TerminalSquareIcon: TerminalSquare,
  UkgLogo,
  ValTownLogo,
  VantaLogo,
  WebflowLogo,
  YoutrustLogo,
  ZendeskLogo,
};

// Compile-time check: ensure INTERNAL_ALLOWED_ICONS matches InternalActionIcons keys.
const _internalCheck: Record<InternalAllowedIconType, unknown> =
  InternalActionIcons;
void _internalCheck;

export const getAvatarFromIcon = (
  icon: InternalAllowedIconType | CustomResourceIconType,
  size: ComponentProps<typeof Avatar>["size"] = "sm"
) => {
  if (isCustomResourceIconType(icon)) {
    return <ResourceAvatar icon={ActionIcons[icon]} size={size} />;
  }

  return <ResourceAvatar icon={InternalActionIcons[icon]} size={size} />;
};

export const getIcon = (
  icon: InternalAllowedIconType | CustomResourceIconType
): ComponentType<ComponentProps<typeof Icon>> => {
  if (isCustomResourceIconType(icon)) {
    return ActionIcons[icon];
  }

  return InternalActionIcons[icon];
};
